import { type KyInstance, type Options } from 'ky';
import { buildQueryPairs } from '../lib/buildQueryPairs';
import { authRequest, banksRequest, payinRequest, payoutRequest } from './request';

export type ErrorType<Error> = Error;
export type BodyType<BodyData> = BodyData;

type CustomInstanceOptions = RequestInit & {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    params?: Record<string, unknown>;
    data?: unknown;
    context?: Options['context'];
    responseType?: 'json' | 'blob' | 'text' | 'arrayBuffer' | 'formData';
    signal?: AbortSignal | null;
    onResponse?: (response: Response) => void | Promise<void>;
};

const createKyMutator =
    (requestInstance: KyInstance) =>
    async <Data>(url: string, options: CustomInstanceOptions): Promise<Data> => {
        const normalizedUrl = url.replace(/^\/+/, '');

        const kyOptions: Options = {
            method: options.method,
            searchParams: buildQueryPairs(options.params),
            headers: options.headers,
            body: options.body,
            json: options.body === undefined ? options.data : undefined,
            context: options.context,
            signal: options.signal,
        };

        const response = await requestInstance(normalizedUrl, kyOptions);

        await options.onResponse?.(response);

        if (options.responseType === 'text') {
            return (await response.text()) as Data;
        }

        if (options.responseType === 'blob') {
            return (await response.blob()) as Data;
        }

        if (options.responseType === 'arrayBuffer') {
            return (await response.arrayBuffer()) as Data;
        }

        if (options.responseType === 'formData') {
            return (await response.formData()) as Data;
        }

        const responseText = await response.text();

        if (!responseText) {
            return undefined as Data;
        }

        return JSON.parse(responseText) as Data;
    };

const executeAuthMutator = createKyMutator(authRequest);
const executeBanksMutator = createKyMutator(banksRequest);
const executePayoutMutator = createKyMutator(payoutRequest);
const executePayinMutator = createKyMutator(payinRequest);

export async function authKyMutator<Data>(url: string, options: CustomInstanceOptions): Promise<Data> {
    return executeAuthMutator<Data>(url, options);
}

export async function banksKyMutator<Data>(url: string, options: CustomInstanceOptions): Promise<Data> {
    return executeBanksMutator<Data>(url, options);
}

export async function payoutKyMutator<Data>(url: string, options: CustomInstanceOptions): Promise<Data> {
    return executePayoutMutator<Data>(url, options);
}

export async function payinKyMutator<Data>(url: string, options: CustomInstanceOptions): Promise<Data> {
    return executePayinMutator<Data>(url, options);
}
